import { useState } from 'react';
import { useGetDiceModeQuery, useSetDiceModeMutation } from '../services/ludo/ludoApi';

const MODES = [
  {
    value: 'normal',
    label: 'Normal',
    description: 'No manipulation — fair game',
    icon: '⚖️',
    color: 'emerald',
    bg: 'bg-emerald-50',
    border: 'border-emerald-400',
    activeBg: 'bg-emerald-500',
    textActive: 'text-white',
    badge: 'bg-emerald-100 text-emerald-700',
  },
  {
    value: 'medium',
    label: 'Medium',
    description: '70% loss · 30% win',
    icon: '🎯',
    color: 'amber',
    bg: 'bg-amber-50',
    border: 'border-amber-400',
    activeBg: 'bg-amber-500',
    textActive: 'text-white',
    badge: 'bg-amber-100 text-amber-700',
  },
  {
    value: 'hard',
    label: 'Hard',
    description: '100% loss',
    icon: '🔥',
    color: 'red',
    bg: 'bg-red-50',
    border: 'border-red-400',
    activeBg: 'bg-red-500',
    textActive: 'text-white',
    badge: 'bg-red-100 text-red-700',
  },
];

const LudoSettingPage = () => {
  const { data, isLoading, isFetching } = useGetDiceModeQuery();
  const [setDiceMode, { isLoading: isSetting }] = useSetDiceModeMutation();
  const [toast, setToast] = useState(null);

  const currentMode = data?.mode || null;

  const handleSelect = async (modeValue) => {
    if (modeValue === currentMode || isSetting) return;
    try {
      const res = await setDiceMode({ mode: modeValue }).unwrap();
      setToast({ type: 'success', msg: res.message || `Mode set to ${modeValue}` });
    } catch {
      setToast({ type: 'error', msg: 'Failed to update mode. Try again.' });
    }
    setTimeout(() => setToast(null), 3000);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-5 right-5 z-50 px-5 py-3 rounded-xl shadow-lg text-sm font-medium flex items-center gap-2 transition-all
          ${toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}
        >
          <span>{toast.type === 'success' ? '✓' : '✕'}</span>
          {toast.msg}
        </div>
      )}

      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center text-white text-base">🎲</div>
            <h1 className="text-xl font-bold text-gray-900">Ludo Setting</h1>
          </div>
          <p className="text-sm text-gray-500 ml-11">Control the dice outcome mode for all active games</p>
        </div>

        {/* Current Mode Banner */}
        <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-6 flex items-center justify-between shadow-sm">
          <div>
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1">Current Mode</p>
            {isLoading || isFetching ? (
              <div className="h-6 w-24 bg-gray-200 rounded animate-pulse" />
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-gray-900 capitalize">{currentMode || '—'}</span>
                {currentMode && (() => {
                  const m = MODES.find(x => x.value === currentMode);
                  return m ? (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${m.badge}`}>{m.label}</span>
                  ) : null;
                })()}
              </div>
            )}
          </div>
          <span className="text-2xl">{MODES.find(x => x.value === currentMode)?.icon || '🎲'}</span>
        </div>

        {/* Mode Cards */}
        <div className="flex flex-col gap-3">
          {MODES.map((mode) => {
            const isActive = currentMode === mode.value;
            return (
              <button
                key={mode.value}
                onClick={() => handleSelect(mode.value)}
                disabled={isSetting || isLoading}
                className={`w-full text-left rounded-2xl border-2 p-5 transition-all duration-200 flex items-center gap-4
                  ${isActive
                    ? `${mode.border} ${mode.bg} shadow-md scale-[1.01]`
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                  }
                  ${isSetting ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                {/* Icon */}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0
                  ${isActive ? mode.activeBg : 'bg-gray-100'}`}
                >
                  {mode.icon}
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`font-bold text-base ${isActive ? 'text-gray-900' : 'text-gray-700'}`}>
                      {mode.label}
                    </span>
                    {isActive && (
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold ${mode.badge}`}>
                        ACTIVE
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">{mode.description}</p>
                </div>

                {/* Radio */}
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0
                  ${isActive ? `${mode.border} ${mode.activeBg}` : 'border-gray-300'}`}
                >
                  {isActive && <div className="w-2 h-2 bg-white rounded-full" />}
                </div>
              </button>
            );
          })}
        </div>

        {isSetting && (
          <p className="text-center text-sm text-gray-400 mt-5 animate-pulse">Updating mode...</p>
        )}
      </div>
    </div>
  );
};

export default LudoSettingPage;
